import { useMatch, useNavigate } from '@reach/router';
import { PassengerModel } from '../models/PassengerModel';
import { useMemo } from 'react';
import { steps, translateRoute } from '../utils/routes';
import routes from '../messages/routes';

type RedirectRule = (params: any) => keyof PathsDefinitions;

type PathsDefinitions = {
  AccountClaimDocuments: [
    steps.Account,
    steps.Claim,
    ':claim',
    routes[steps.ClaimDocuments]
  ];
  AccountClaimDocumentsPassengerAuthorization: [
    steps.Account,
    steps.Claim,
    ':claim',
    steps.Passenger,
    ':passenger',
    steps.Authorization
  ];
  AccountClaimDocumentsPassengerAuthorizationGuardian: [
    steps.Account,
    steps.Claim,
    ':claim',
    steps.Passenger,
    ':passenger',
    steps.Authorization,
    steps.Guardian,
    ':guardian'
  ];
};

export const PathDefs: PathsDefinitions = {
  AccountClaimDocuments: [
    routes[steps.Account],
    routes[steps.Claim],
    ':claim',
    routes[steps.ClaimDocuments],
  ],
  AccountClaimDocumentsPassengerAuthorization: [
    routes[steps.Account],
    routes[steps.Claim],
    ':claim',
    routes[steps.Passenger],
    ':passenger',
    routes[steps.Authorization],
  ],
  AccountClaimDocumentsPassengerAuthorizationGuardian: [
    routes[steps.Account],
    routes[steps.Claim],
    ':claim',
    routes[steps.Passenger],
    ':passenger',
    routes[steps.Authorization],
    routes[steps.Guardian],
    ':guardian',
  ],
};

type RedirectPair = {
  [Property in keyof PathsDefinitions]?:
    | RedirectRule
    | {
        back: RedirectRule;
        next: RedirectRule;
      };
};

const redirectPairs: RedirectPair = {
  AccountClaimDocumentsPassengerAuthorization: ({
    passengerData,
  }: {
    passengerData: PassengerModel;
  }) => {
    if (passengerData.person.is_minor)
      return 'AccountClaimDocumentsPassengerAuthorizationGuardian';
    return 'AccountClaimDocuments';
  },
  AccountClaimDocumentsPassengerAuthorizationGuardian: ({
    guardian,
  }: {
    guardian: string;
  }) => {
    if (Number(guardian) <= 2)
      return 'AccountClaimDocumentsPassengerAuthorizationGuardian';
    return 'AccountClaimDocuments';
  },
};

export const useStepNavigate = pathToMatch => {
  const translatedPairs = Object.entries(redirectPairs).reduce(
    (acc, [path, translatePath]) => {
      const translatedPath = translateRoute(PathDefs[path]);
      return {
        ...acc,
        [`/${translatedPath}`]: translatePath,
      };
    },
    {}
  );

  /**
   * Transform location.pathname to parametrized route string, so we can use
   * match from Reach Router
   */
  const match = useMatch(`/${translateRoute(pathToMatch)}`);

  const navigate = useNavigate();

  const navigateNext = useMemo(() => {
    if (match && translatedPairs[match.path]) {
      return (params, options?: {}) => {
        const redirectRule =
          translatedPairs[match.path]?.next || translatedPairs[match.path];

        const redirectPath = translateRoute(PathDefs[redirectRule(params)]);

        navigate(
          '/' +
            redirectPath.replace(
              /(:(\w+))/g,
              (matches, colon: string, paramName: string[]) => {
                return params[paramName] || match[paramName];
              }
            ),
          options
        );
      };
    }
  }, [match?.path]);

  return { navigateNext };
};
